import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, inject, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';

/**
 * Renderiza Markdown con estilos corporativos MotoBoy (amber/naranja).
 * - Headers con jerarquía visual clara.
 * - Tablas con zebra rows y borders suaves.
 * - Blockquotes con borde amber.
 * - Código inline y bloques con fondo gris.
 */
@Component({
    selector: 'mb-markdown-viewer',
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [
        `
            :host {
                display: block;
                color: #1f2937;
                font-family: 'Inter', system-ui, -apple-system, sans-serif;
                font-size: 14px;
                line-height: 1.7;
            }
            :host ::ng-deep h1 {
                font-size: 1.75rem;
                font-weight: 800;
                color: #111827;
                margin: 0 0 1rem 0;
                padding-bottom: 0.5rem;
                border-bottom: 2px solid #f59e0b;
            }
            :host ::ng-deep h2 {
                font-size: 1.35rem;
                font-weight: 700;
                color: #111827;
                margin: 1.75rem 0 0.75rem 0;
                padding-bottom: 0.4rem;
                border-bottom: 1px solid #e5e7eb;
            }
            :host ::ng-deep h3 {
                font-size: 1.1rem;
                font-weight: 700;
                color: #374151;
                margin: 1.25rem 0 0.5rem 0;
            }
            :host ::ng-deep h4 {
                font-size: 1rem;
                font-weight: 700;
                color: #374151;
                margin: 1rem 0 0.5rem 0;
            }
            :host ::ng-deep p {
                margin: 0 0 0.85rem 0;
                color: #374151;
            }
            :host ::ng-deep strong {
                color: #111827;
                font-weight: 700;
            }
            :host ::ng-deep em {
                color: #4b5563;
                font-style: italic;
            }
            :host ::ng-deep a {
                color: #f97316;
                text-decoration: underline;
                font-weight: 500;
            }
            :host ::ng-deep a:hover {
                color: #c2410c;
            }
            :host ::ng-deep ul,
            :host ::ng-deep ol {
                margin: 0 0 1rem 0;
                padding-left: 1.5rem;
            }
            :host ::ng-deep li {
                margin-bottom: 0.3rem;
                color: #374151;
            }
            :host ::ng-deep li > ul,
            :host ::ng-deep li > ol {
                margin-top: 0.3rem;
                margin-bottom: 0.3rem;
            }
            :host ::ng-deep blockquote {
                border-left: 4px solid #f59e0b;
                background: #fffbeb;
                padding: 0.75rem 1rem;
                margin: 0.75rem 0;
                color: #92400e;
                border-radius: 0 6px 6px 0;
            }
            :host ::ng-deep blockquote p {
                margin: 0.25rem 0;
                color: #92400e;
            }
            :host ::ng-deep blockquote strong {
                color: #78350f;
            }
            :host ::ng-deep hr {
                border: 0;
                border-top: 1px solid #e5e7eb;
                margin: 1.5rem 0;
            }
            :host ::ng-deep code {
                background: #f3f4f6;
                color: #be123c;
                padding: 2px 6px;
                border-radius: 4px;
                font-family: ui-monospace, Menlo, monospace;
                font-size: 0.85em;
            }
            :host ::ng-deep pre {
                background: #1f2937;
                color: #f3f4f6;
                padding: 1rem;
                border-radius: 8px;
                overflow-x: auto;
                margin: 1rem 0;
            }
            :host ::ng-deep pre code {
                background: transparent;
                color: inherit;
                padding: 0;
            }
            :host ::ng-deep table {
                width: 100%;
                border-collapse: collapse;
                margin: 0.75rem 0 1.25rem 0;
                font-size: 0.9rem;
                border: 1px solid #e5e7eb;
                border-radius: 6px;
                overflow: hidden;
            }
            :host ::ng-deep thead {
                background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
            }
            :host ::ng-deep th {
                text-align: left;
                padding: 0.6rem 0.8rem;
                font-weight: 700;
                color: #92400e;
                border-bottom: 2px solid #f59e0b;
            }
            :host ::ng-deep td {
                padding: 0.5rem 0.8rem;
                border-top: 1px solid #e5e7eb;
                vertical-align: top;
                color: #374151;
            }
            :host ::ng-deep tbody tr:nth-child(even) {
                background: #f9fafb;
            }
            :host ::ng-deep tbody tr:hover {
                background: #fffbeb;
            }
        `,
    ],
    template: `<div [innerHTML]="renderedHtml()"></div>`,
})
export class MarkdownViewerComponent implements OnChanges {
    private _sanitizer = inject(DomSanitizer);

    @Input() markdown = '';

    renderedHtml = signal<SafeHtml>('');

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['markdown']) {
            this.render();
        }
    }

    private render(): void {
        try {
            const html = marked.parse(this.markdown ?? '', { async: false, breaks: false, gfm: true }) as string;
            this.renderedHtml.set(this._sanitizer.bypassSecurityTrustHtml(html));
        } catch (err) {
            this.renderedHtml.set(this._sanitizer.bypassSecurityTrustHtml(`<pre>${this.markdown}</pre>`));
        }
    }
}
